<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Expense Categories</h5>
          <p class="page-sub">Manage categories for classifying expenses</p>
        </div>
        <div class="page-actions">
  
          <!-- Search -->
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search categories..." @input="tableData.page = 1; fetchData()" v-model="tableData.search" />
          </div>
  
          <!-- Status Filter -->
          <select v-model="tableData.status" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="[1]">Active</option>
            <option :value="[0]">Inactive</option>
            <option :value="[0,1]">All</option>
          </select>
  
          <!-- Export -->
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'p', 'expense_category', 'Expense Category')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('expense_category.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
          <!-- Create Category -->
          <button
            class="btn-primary-action"
            :class="permission.create_expense_category != 1 ? 'disabled' : ''"
            :disabled="permission.create_expense_category != 1"
            @click="openCreateModal"
          >
            <font-awesome-icon icon="plus" /> Create Category
          </button>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading categories...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status == 403" class="state-block">
          <div class="state-icon state-icon--red">
            <font-awesome-icon icon="lock" />
          </div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view this resource.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--orange">
            <font-awesome-icon icon="tags" />
          </div>
          <p class="state-title">No categories found</p>
          <p class="state-sub">Create your first expense category to get started.</p>
          <button v-if="permission.create_expense_category == 1" class="btn-primary-action mt-2" @click="openCreateModal">
            <font-awesome-icon icon="plus" /> Create Category
          </button>
        </div>
  
        <!-- Table -->
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
            </span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option :value="pagination.total">All</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Category Name</th>
                  <th>Status</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in details" :key="row.id">
  
                  <td class="fw-bold">{{ row.name }}</td>
  
                  <td>
                    <div class="toggle-wrap">
                      <label class="toggle">
                        <input
                          type="checkbox"
                          :true-value="1" :false-value="0"
                          v-model="statuses[index]"
                          @change="updateCategoryStatus(row.id)"
                        />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-label" :class="statuses[index] == 1 ? 'toggle-label--on' : 'toggle-label--off'">
                        {{ statuses[index] == 1 ? 'Active' : 'Inactive' }}
                      </span>
                    </div>
                  </td>
  
                  <td class="col-actions">
                    <div class="action-btns">
                      <button
                        class="action-btn action-btn--edit"
                        :disabled="permission.update_expense_category != 1"
                        @click="openUpdateModal(index)"
                        title="Edit"
                      >
                        <font-awesome-icon icon="pen" />
                      </button>
                      <button
                        class="action-btn action-btn--delete"
                        :disabled="permission.delete_expense_category != 1"
                        @click="openDeleteModal(index)"
                        title="Delete"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
          </div>
  
        </template>
      </div>
  
  
      <!-- ── Create Modal ── -->
      <div class="modal fade" id="create_group" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(224,140,0,.1);color:#e08c00;">
                <font-awesome-icon icon="tags" />
              </div>
              <div>
                <h6 class="modal-clean-title">Create Expense Category</h6>
                <p class="modal-clean-sub">Add a new category for classifying expenses</p>
              </div>
              <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <form @submit.prevent="createCategory">
              <div class="modal-body">
                <div class="field-group">
                  <label class="field-label">Category Name <span class="req">*</span></label>
                  <input type="text" class="field-input" placeholder="e.g. Office Supplies" v-model="name" />
                </div>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button type="button" class="btn-outline" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn-primary-action" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ loading ? 'Creating...' : 'Create Category' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
  
      <!-- ── Update Modal ── -->
      <div class="modal fade" id="update_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
                <font-awesome-icon icon="pen" />
              </div>
              <div>
                <h6 class="modal-clean-title">Update Category</h6>
                <p class="modal-clean-sub">{{ currentData.name }}</p>
              </div>
              <button type="button" class="btn-close ms-auto" @click="closeUpdateModal" aria-label="Close"></button>
            </div>
            <form @submit.prevent="updateCategory">
              <div class="modal-body">
                <div class="field-group">
                  <label class="field-label">Category Name <span class="req">*</span></label>
                  <input type="text" class="field-input" placeholder="Category name" v-model="currentData.name" />
                </div>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button type="button" class="btn-outline" @click="closeUpdateModal">Cancel</button>
                <button type="submit" class="btn-primary-action" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ loading ? 'Saving...' : 'Update Category' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
  
      <!-- ── Delete Modal ── -->
      <div class="modal fade" id="delete_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-icon-wrap">
              <div class="modal-icon modal-icon--danger">
                <font-awesome-icon icon="trash" />
              </div>
            </div>
            <div class="modal-body-clean">
              <p class="modal-heading">Delete Category?</p>
              <p class="modal-subtext">
                Are you sure you want to delete <strong>{{ currentData.name }}</strong>? This cannot be undone.
              </p>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-btn modal-btn--cancel" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="modal-btn modal-btn--danger" data-bs-dismiss="modal" @click.prevent="deleteCategory">
                <font-awesome-icon icon="trash" class="me-2" /> Delete
              </button>
            </div>
          </div>
        </div>
      </div>
  
    </div>
  </template>
  
  <script>
  import { reusableMixin } from '../../../lib/reusableMixin';
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  
  export default {
    components: { Header },
    mixins: [reusableMixin],
  
    data() {
      return {
        name:             '',
        print_content:    [],
        headerArrayColumn: ['S/N','Category Name','Status'],
        details:          [],
        statuses:         [],
        loading:          false,
        currentData:      {},
        permission_status: null,
  
        tableData: { page: 1,
          draw: 0, length: 10, search: '', column: 0, dir: 'desc', id: '', status: [0,1],
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast:  useToast(),
        store:  useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  
    created() { this.fetchData(); },
  
    methods: {
      dataToPrint(details) {
        this.print_content = details.map((d, idx) => ({
          sn: idx + 1, name: d.name, status: d.status == 1 ? 'Active' : 'Inactive',
        }));
      },
  
      fetchData(url = '/accounting/expense/category/all') {
        this.loading = true;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            const data  = response.data;
            this.details = data.data.data;
            this.statuses = this.details.map(c => c.is_active);
            this.dataToPrint(this.details);
            this.configPagination(data.data);
            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            if (error?.response?.status == 403) {
              this.permission_status = 403;
              this.toast.error(error.response.data.message);
            }
          });
      },
  
      openCreateModal() { window.$('#create_group').modal('show'); },
  
      async createCategory() {
        if (!this.name?.trim()) { this.toast.warning('Category name is required'); return; }
        this.loading = true;
        axios.post('/accounting/ledger/create', { name: this.name, group_id: 5 })
          .then(() => {
            this.loading = false;
            this.name = '';
            this.toast.success('Category created successfully');
            this.fetchData();
            window.$('#create_group').modal('hide');
          })
          .catch(error => {
            this.loading = false;
            this.toast.warning(error.response?.data?.message ?? 'Something went wrong');
          });
      },
  
      async updateCategory() {
        this.loading = true;
        axios.put('/accounting/ledger/update', this.currentData)
          .then(() => {
            this.loading = false;
            this.toast.success('Category updated successfully');
            this.fetchData();
            this.closeUpdateModal();
          })
          .catch(error => {
            this.loading = false;
            const status = error.response?.status;
            if (status == 400) this.toast.error('This category cannot be edited');
            else this.toast.error(error.response?.data?.message ?? 'Something went wrong');
          });
      },
  
      async updateCategoryStatus(id) {
        axios.put(`accounting/ledger/update-status/${id}`)
          .then(res => { this.toast.success(res.data.message); this.fetchData(); })
          .catch(() => { this.toast.error('Could not update status'); });
      },
  
      async deleteCategory() {
        axios.delete(`/accounting/ledger/${this.currentData.id}`)
          .then(() => { this.toast.success('Category deleted successfully'); this.fetchData(); })
          .catch(error => { this.toast.error(error.response?.data?.message ?? 'Could not delete category'); });
      },
  
      openUpdateModal(index) { this.currentData = { ...this.details[index] }; window.$('#update_modal').modal('show'); },
      openDeleteModal(index) { this.currentData = this.details[index]; window.$('#delete_modal').modal('show'); },
      closeUpdateModal()     { window.$('#update_modal').modal('hide'); },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  };
  </script>
  
  <style scoped>
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover:not(:disabled):not(.disabled) { background:#003b8e; }
  .btn-primary-action.disabled,.btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  .me-2 { margin-right:8px; } .ms-auto { margin-left:auto; } .mt-2 { margin-top:8px; }
  
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  .state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon   { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--orange { background:rgba(224,140,0,.1); color:#e08c00; }
  .state-icon--red    { background:rgba(224,53,53,.1);  color:#e03535; }
  .state-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .mt-3         { margin-top:12px; }
  
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .toolbar-right { display:flex; align-items:center; gap:8px; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 14px; font-size:13.5px; vertical-align:middle; }
  .col-actions { width:80px; }
  .fw-bold     { font-weight:700; color:#1a2b45; }
  .text-muted-cell { color:#4a5e78; }
  
  /* ── Toggle ── */
  .toggle-wrap  { display:flex; align-items:center; gap:8px; }
  .toggle       { position:relative; display:inline-block; width:36px; height:20px; flex-shrink:0; }
  .toggle input { opacity:0; width:0; height:0; }
  .toggle-slider { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:20px; transition:background .2s; }
  .toggle-slider::before { content:''; position:absolute; width:14px; height:14px; left:3px; bottom:3px; background:#fff; border-radius:50%; transition:transform .2s; }
  .toggle input:checked + .toggle-slider { background:#004AAD; }
  .toggle input:checked + .toggle-slider::before { transform:translateX(16px); }
  .toggle-label     { font-size:12.5px; font-weight:600; }
  .toggle-label--on  { color:#16a34a; }
  .toggle-label--off { color:#7a90b0; }
  
  /* ── Action Buttons ── */
  .action-btns  { display:flex; align-items:center; gap:5px; }
  .action-btn   { width:28px; height:28px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:11px; cursor:pointer; transition:background .15s; background:#fff; }
  .action-btn:disabled { opacity:.4; cursor:not-allowed; }
  .action-btn--edit   { color:#004AAD; } .action-btn--edit:hover:not(:disabled)   { background:#edf4ff; border-color:#c5d6f5; }
  .action-btn--delete { color:#e03535; } .action-btn--delete:hover:not(:disabled) { background:#fff0f0; border-color:#fac5c5; }
  
  /* ── Footer ── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Dropdown ── */
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  /* ── Modals ── */
  .modal-clean { border:none; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.14); overflow:hidden; }
  .modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
  .modal-title-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }
  
  .field-group  { display:flex; flex-direction:column; gap:6px; }
  .field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
  .req          { color:#e03535; }
  .field-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  
  /* ── Delete Modal ── */
  .modal-icon-wrap { display:flex; justify-content:center; padding:28px 24px 0; }
  .modal-icon { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:26px; }
  .modal-icon--danger { background:rgba(224,53,53,.1); color:#e03535; }
  .modal-body-clean { padding:16px 28px 8px; text-align:center; }
  .modal-heading    { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
  .modal-subtext    { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.6; }
  .modal-actions    { display:flex; gap:10px; padding:18px 24px 24px; justify-content:center; }
  .modal-btn        { display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; flex:1; }
  .modal-btn--cancel { background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; } .modal-btn--cancel:hover { background:#e8eef6; }
  .modal-btn--danger { background:#e03535; color:#fff; } .modal-btn--danger:hover { background:#c42b2b; }
  
  @media(max-width:991px) { .page-header,.page-actions { flex-direction:column; align-items:stretch; } .search-input { width:100%; } }
  </style>